import { SalesStrategyAndPrioritizationForm } from "@/components/campaign/setup/planning/SalesStrategyAndPrioritizationForm";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { Configuration } from "@/types/campaign";
import { useStepperSubmitHandlerState } from "@/utils/stepper";
import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { SetStateAction, useCallback } from "react";

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/planning/sales-strategy-and-prioritization",
)({
  component: RouteComponent,
  loader: ({ context }) => {
    const title = context.planningStepper.stepperState.flatStepsByTo.get(
      Route.fullPath,
    )?.name;
    return {
      crumb: title,
      meta: { title },
    };
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const loaderData = Route.useLoaderData();
  const { updateCampaign } = context.useEditCampaign();
  const stepper = context.usePlanningStepper();

  const setConfigurations = useCallback(
    async (action: SetStateAction<Configuration[]>) => {
      await updateCampaign({
        configurations:
          typeof action === "function"
            ? action(stepper.context.configurations)
            : action,
      });
    },
    [stepper.context.configurations, updateCampaign],
  );

  const [submitHandler, setSubmitHandler] = useStepperSubmitHandlerState();

  const blocker = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (!submitHandler.isDirty) return false;
      if (next.routeId.startsWith(Route.id)) return false;
      return true;
    },
    withResolver: true,
  });

  return (
    <PageHeaderProvider
      options={{
        title: loaderData.meta.title,
        actions: (
          <>
            <Button
              isDisabled={submitHandler.isDisabled || !submitHandler.isDirty}
              isPending={submitHandler.isPending}
              onPress={() => submitHandler.submit()}
            >
              Gem
            </Button>
          </>
        ),
      }}
    >
      <SalesStrategyAndPrioritizationForm
        configurations={stepper.context.configurations}
        updateConfigurations={setConfigurations}
        setSubmitHandler={setSubmitHandler}
      />
      <AlertModal
        isOpen={blocker.status === "blocked"}
        title="Du har ikke-gemte ændringer"
        description="Dine ændringer vil gå tabt. Er du sikker på at du vil fortsætte?"
        onClose={blocker.reset}
        onConfirm={() => {
          blocker.proceed?.();
        }}
      />
    </PageHeaderProvider>
  );
}
